<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';
import WhatsappMassCampaignForm from 'custom/components/whatsappCampaigns/WhatsappMassCampaignForm.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const campaignMode = ref('labels');

const addCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleSubmit = campaignDetails => {
  addCampaign(campaignDetails);
};

const handleClose = () => emit('close');

const handleMassCreated = async () => {
  await store.dispatch('campaigns/get');
  useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
    type: CAMPAIGN_TYPES.ONE_OFF,
  });
  useAlert(t('CAMPAIGN.WHATSAPP.MASS.API.SUCCESS_MESSAGE'));
  handleClose();
};
</script>

<template>
  <div
    class="z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-md max-h-[80vh] overflow-y-auto"
    :class="
      campaignMode === 'mass' ? 'w-[min(48rem,calc(100vw-2rem))]' : 'w-[25rem]'
    "
  >
    <div class="p-6 flex flex-col gap-6">
      <h3 class="text-base font-medium text-n-slate-12 flex-shrink-0">
        {{ t(`CAMPAIGN.WHATSAPP.CREATE.TITLE`) }}
      </h3>
      <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-black2" role="tablist">
        <button
          type="button"
          class="flex-1 px-3 py-2 text-xs font-medium rounded-md text-n-slate-11"
          :class="
            campaignMode === 'labels'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : ''
          "
          @click="campaignMode = 'labels'"
        >
          {{ t('CAMPAIGN.WHATSAPP.MODE.LABELS') }}
        </button>
        <button
          type="button"
          class="flex-1 px-3 py-2 text-xs font-medium rounded-md text-n-slate-11"
          :class="
            campaignMode === 'mass'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : ''
          "
          @click="campaignMode = 'mass'"
        >
          {{ t('CAMPAIGN.WHATSAPP.MODE.CSV') }}
        </button>
      </div>
      <WhatsAppCampaignForm
        v-if="campaignMode === 'labels'"
        @submit="handleSubmit"
        @cancel="handleClose"
      />
      <WhatsappMassCampaignForm
        v-else
        @created="handleMassCreated"
        @cancel="handleClose"
      />
    </div>
  </div>
</template>
